// Story 7-FU-I (ADO-1222) AC4, redesigned by Story 7-FU-K (ADO-1528) AC2/AC4 — bounded
// AUTO-retry, shared by the two paths that need it: the substitutes fetch
// (`useSubstitutesStore`) and the substitute-candidate availability read (the availability
// read cycle, `availability/availability-read-cycle.ts`). Both want the same shape — the PO
// decision is to retry automatically with NO manual "Try again" CTA, so a transient failure is
// retried a few times with a short backoff and is never surfaced mid-ladder; at the ceiling it
// settles into a QUIET terminal error, never a permanent spinner.
//
// WHAT THIS COUNTS: REQUESTS PUT ON THE WIRE. Not failures, not reports of a failure. The
// register is written by whoever is about to ISSUE a request, before it goes out, and the
// ceiling is therefore arithmetically independent of how many consumers observe the read and
// of when each of them arrived. Counting at REPORT time is what ADO-1528 exists to undo: the
// previous `recordFailure(key, seen)` compare-and-swapped on a per-observer `seen` so one
// shared read was counted once, which kept co-observers in lockstep but silently DISCARDED a
// lagging observer's report — so an observer that entered the cycle mid-ladder issued a
// request that was never counted (measured: 4 requests against a ceiling of 3 for two cards
// ~100ms apart, N+2 for overlapping cycles from a band flap). Counting at issue needs no
// compare-and-swap, so there is nothing to discard, and it removes the reachable
// `retryBackoffMs(0)` the CAS's `return current` produced.
//
// The register is what makes the ceiling per-QUESTION-per-WINDOW rather than per-invocation.
// Both callers are re-invoked freely — a card remounts, an availability band toggles the
// trigger back on — so a counter scoped to one run would let a hard-down endpoint be
// re-laddered without limit. The stamp is taken when a cycle GIVES UP, not when it opens:
// three attempts each hanging to a gateway timeout would otherwise age the window out before
// the terminal failure, and the next invocation would start over.
//
// It owns BOTH halves of the bound — the ISSUE COUNT of the open cycle and the give-up STAMP —
// because they are only correct together. The stamp alone bounds nothing before the terminal
// failure: a cycle abandoned mid-backoff (the consumer unmounted) has no stamp yet, so if its
// count lived in the caller it would restart at zero on the next invocation and a remount loop
// could ladder forever (PR #160 round 11, F7). Keeping the count here makes it survive the
// caller, and `forget` drops both so a spent cycle can never half-persist.

/** Total requests one question may put on the wire per window: the initial call + 2 retries. */
export const MAX_RETRY_ATTEMPTS = 3;

/**
 * The backoff ladder, indexed by the requests ALREADY put on the wire for this question (see
 * `nextBackoffMs`). Its length is tied to `MAX_RETRY_ATTEMPTS` by a test in
 * `bounded-retry.spec.ts`, not by a comment asking the two to stay in sync: lowering the
 * ceiling without dropping a rung — or raising it without adding one — fails there.
 */
export const RETRY_BACKOFF_MS = [500, 1000] as const;

/**
 * How long to wait before the retry that follows `issued` requests — or `undefined` when the
 * ladder has no rung for it, which IS the ceiling.
 *
 * Callers decide TERMINALITY by this `undefined` rather than by comparing counts separately,
 * so a retry can never be scheduled on a rung the ladder does not define (ADO-1528 AC4). The
 * predecessor was a two-branch ternary — `(failures) => (failures === 1 ? 500 : 1000)` — whose
 * domain was 1 and 2 and which silently answered the SECOND rung for an out-of-domain 0.
 */
export const nextBackoffMs = (issued: number): number | undefined => RETRY_BACKOFF_MS[issued - 1];

/** How long a given-up question is refused a fresh ladder. */
export const RETRY_COOLDOWN_MS = 30_000;

/**
 * Target ceiling on keys held in one register — the bound on a count that cannot be aged out
 * (see `createRetryRegister`). Deliberately far above the number of questions that can be
 * mid-ladder at once (a grid shows tens of cards, each asking one question), so eviction
 * only ever reclaims cycles nothing came back for.
 *
 * A TARGET rather than a hard ceiling: eviction never drops a key holding a live cooldown,
 * because that would return a given-up question to a full ladder inside its own window. The
 * attempt bound wins, and the register drains back below the cap as those windows age out.
 */
export const MAX_REGISTER_KEYS = 500;

/**
 * `'cooling'` — still inside the window; keep the terminal error.
 * `'expired'` — a stamp existed but has aged out (and was dropped); start a fresh cycle.
 * `'none'` — this key has never given up.
 */
export type CooldownStatus = 'none' | 'cooling' | 'expired';

export interface RetryRegister {
  /**
   * Where `key`'s cooldown stands. `'expired'` also FORGETS the key — a window that aged
   * out takes its issue count with it, so the fresh cycle the caller is about to start
   * is genuinely fresh rather than inheriting spent requests.
   *
   * Reading this MUTATES on expiry, which is safe because each register has exactly ONE reader,
   * and each reader reads it ONCE per decision: `drive` for the availability read cycle's
   * register, `refuseIfSpent` for the substitutes one (reached from `load()` and from the retry
   * timer, both of which route through that single function precisely so there is one reader
   * rather than two). ADO-1528 AC5 is satisfied by that ownership rather than by making expiry
   * non-destructive — under the previous multi-reader arrangement only the FIRST caller saw
   * `'expired'` and re-seated its count, and the second was left holding a stale one.
   *
   * "Once per decision" is part of the contract, not incidental tidiness: two reads in one
   * decision give the second caller `'none'` where the first got `'expired'`, so a branch can be
   * correct only by depending on its sibling having already mutated.
   */
  readonly status: (key: string) => CooldownStatus;
  /** Requests put on the wire so far for `key`'s open cycle (`0` = no open cycle). A caller
   *  ENTERING a cycle reads this to see how much of the ladder is already spent, so a resumed
   *  cycle picks up where the abandoned one left it instead of restarting at zero. */
  readonly issued: (key: string) => number;
  /**
   * Record that one request is going ON THE WIRE for `key`, and return the new total.
   *
   * Called BEFORE the request is issued, by the single owner of the cycle. There is no `seen`
   * and no compare-and-swap: the count belongs to the wire, so nothing can be handed a stale
   * total and nothing needs to be discarded. A consumer that goes away while that request is
   * in flight cannot un-count it (PR #160 round 14) — the request was spent.
   */
  readonly recordIssue: (key: string) => number;
  /**
   * Withdraw the most recent `recordIssue` for `key`, because the request TAUGHT US NOTHING
   * ABOUT THE ENDPOINT.
   *
   * The only caller is a read whose transport reported `'superseded'`, which covers three cases
   * — and, deliberately, not all three are "no request was spent" (ADO-1528 AC6):
   *  - the depot moved between enqueue and flush, so the request was never put on the wire;
   *  - the depot moved WHILE the request was in flight, so a request was spent but its result
   *    belongs to a depot we have left and is discarded;
   *  - the claim the request was issued for was retired and re-taken while it was in flight, so
   *    its result cannot be written for the claim that is live now.
   * In the last two a request really did go out. What they share with the first is that the
   * answer cannot be used and nothing was learned about whether the endpoint can answer, which
   * is what the ladder exists to measure — so the rung is handed back.
   *
   * This is NOT "cancellation un-charges a failure": a consumer's departure produces no
   * superseded outcome at all — its request completes and is accounted like any other (round 14).
   * The distinction is whether the answer was USABLE, which the transport reports, rather than
   * who was still watching, which it does not.
   */
  readonly voidIssue: (key: string) => void;
  /** Stamp `key` as having just given up, starting its cooldown. A key already inside its
   *  window is left on its ORIGINAL stamp: re-stamping would push the window out by one
   *  report each time. */
  readonly gaveUp: (key: string) => void;
  /** Forget `key` entirely — count AND stamp (a success, or a fresh cycle that
   *  supersedes it). The two never persist apart: a stamp with no count would let a
   *  fresh cycle run unbounded, a count with no stamp would bound a question nothing
   *  is asking. */
  readonly forget: (key: string) => void;
  /** Forget every key (store reset / test hygiene). */
  readonly clear: () => void;
}

/**
 * A keyed retry register. A key identifies the QUESTION being retried — a SKU, a
 * candidates+depot pair — so callers asking the same question share one bound: its issue
 * count and its give-up stamp.
 *
 * Growth is bounded two ways, because the two kinds of key expire differently:
 *  - a key with a GIVE-UP STAMP is pruned once its window has aged out, on the next write;
 *  - a key with only an ISSUE COUNT cannot be aged out at all. That count exists precisely
 *    to outlive its consumer (that is what bounds a remount loop), and the register has no
 *    way to tell a cycle abandoned mid-backoff from one whose attempt is simply slow —
 *    attempts hanging to a gateway timeout can be minutes apart, and dropping the count
 *    between two of them would restart the ladder, the exact unbounded-slow-endpoint hole
 *    the give-up-time stamping was introduced to close. So those are bounded by COUNT
 *    instead: least-recently-touched keys are evicted on write until the register is back at
 *    `MAX_REGISTER_KEYS` — skipping any key whose cooldown is still live, so the size bound
 *    can never weaken the attempt bound (see `collect`).
 * Without the second bound the register keeps a row per abandoned cycle for the life of the
 * tab, and candidate keys are `JSON.stringify(skus)|depot` — one per product per depot.
 */
export function createRetryRegister(): RetryRegister {
  const gaveUpAt = new Map<string, number>();
  // Doubles as the recency order for the size cap: every write re-inserts, so iteration
  // order runs least- to most-recently-touched. Every key in the register has a count (a
  // give-up stamp is only ever set after a request), so this covers all of them.
  const issuedCounts = new Map<string, number>();
  const forget = (key: string): void => {
    gaveUpAt.delete(key);
    issuedCounts.delete(key);
  };
  /** Prune aged-out stamped keys, then hold the register to `MAX_REGISTER_KEYS`. */
  const collect = (now: number): void => {
    for (const [key, at] of gaveUpAt) {
      if (now - at >= RETRY_COOLDOWN_MS) forget(key);
    }
    // Evict least-recently-touched first, so a cycle still laddering is the LAST thing to
    // go: it was touched on its most recent request. The cap is far above the number of
    // questions that can plausibly be mid-ladder at once, so in practice this only ever
    // reclaims abandoned cycles.
    //
    // A key holding a LIVE COOLDOWN is never evicted, which is the same invariant `voidIssue`
    // refuses to break and for the same reason: `forget` takes both halves, so dropping a
    // stamped key inside its window would hand a question that has already given up a fresh
    // ladder of `MAX_RETRY_ATTEMPTS` requests inside that very window. Every stamp still here
    // is unexpired — the sweep above has just taken the aged-out ones — so the size cap yields
    // to the attempt bound rather than the other way round. The register can therefore sit ABOVE
    // the cap, bounded by the questions that gave up within one cooldown window and draining as
    // those windows age out on the next write.
    if (issuedCounts.size <= MAX_REGISTER_KEYS) return;
    let over = issuedCounts.size - MAX_REGISTER_KEYS;
    for (const key of [...issuedCounts.keys()]) {
      if (over === 0) return;
      if (gaveUpAt.has(key)) continue;
      forget(key);
      over -= 1;
    }
  };
  const touch = (key: string, count: number): void => {
    issuedCounts.delete(key); // re-insert so this key becomes the most recent
    issuedCounts.set(key, count);
  };
  return {
    status(key) {
      const at = gaveUpAt.get(key);
      if (at === undefined) return 'none';
      if (Date.now() - at < RETRY_COOLDOWN_MS) return 'cooling';
      forget(key);
      return 'expired';
    },
    issued(key) {
      return issuedCounts.get(key) ?? 0;
    },
    recordIssue(key) {
      const next = (issuedCounts.get(key) ?? 0) + 1;
      touch(key, next);
      collect(Date.now());
      return next;
    },
    voidIssue(key) {
      const current = issuedCounts.get(key) ?? 0;
      if (current === 0) return; // nothing recorded (an expiry forgot the key meanwhile)
      // Down to zero, drop the key outright rather than hold a 0 row: `issued` reports 0 either
      // way, and an empty row would occupy a slot under the size cap. UNLESS the key is STAMPED —
      // `forget` takes both halves, so dropping it would cancel a live cooldown and let a
      // given-up question ladder again inside its own window. Enforced here rather than left to
      // the callers: the interface promises the count and the stamp never persist apart, so it
      // owes the other direction too. (No caller can reach this today — a stamp implies the
      // ceiling was reached and nothing is outstanding — which is exactly why it is cheap to make
      // structurally impossible instead of relying on that staying true.)
      if (current > 1 || gaveUpAt.has(key)) touch(key, current - 1);
      else forget(key);
    },
    gaveUp(key) {
      const now = Date.now();
      const at = gaveUpAt.get(key);
      // Already inside its window — keep the original stamp so a second report of the same
      // terminal read cannot push the cooldown out.
      if (at !== undefined && now - at < RETRY_COOLDOWN_MS) return;
      gaveUpAt.set(key, now);
      touch(key, issuedCounts.get(key) ?? MAX_RETRY_ATTEMPTS);
      collect(now);
    },
    forget,
    clear() {
      gaveUpAt.clear();
      issuedCounts.clear();
    },
  };
}
